/**
 * Convoy Details Cache Service
 * Caches convoy detail data (vehicles, drivers, docking stations) to avoid repeated API calls
 * 
 * Cache Strategy:
 * - In-memory cache for fast access during session
 * - sessionStorage for persistence across page refreshes
 * - TTL-based expiration for convoy details
 */

class ConvoyDetailsCache {
  constructor() {
    // In-memory caches: Maps for vehicles, drivers, sites, and all drivers list
    this.vehicleCache = new Map();
    this.driverCache = new Map();
    this.siteCache = new Map();
    this.allDriversCache = null;
    this.allDriversCacheTimestamp = null;
    
    // Cache configuration
    this.config = {
      // Convoy details expire after 10 minutes (they change less frequently)
      CONVOY_DETAILS_TTL: 10 * 60 * 1000, // 10 minutes in milliseconds
      // Storage key for sessionStorage
      STORAGE_KEY: 'convoyDetailsCache',
    };
    
    // Load cache from sessionStorage on initialization
    this.loadFromStorage();
  }

  /**
   * Get cache key for a vehicle
   */
  getVehicleKey(vehicleId) {
    return `vehicle_${vehicleId}`;
  }

  /**
   * Get cache key for a driver
   */
  getDriverKey(driverId) {
    return `driver_${driverId}`;
  }

  /**
   * Get cache key for a site
   */
  getSiteKey(siteId) {
    return `site_${siteId}`;
  }

  /**
   * Load cache from sessionStorage
   */
  loadFromStorage() {
    try {
      const stored = sessionStorage.getItem(this.config.STORAGE_KEY);
      if (stored) {
        const cacheData = JSON.parse(stored);
        const now = Date.now();
        
        // Load vehicle cache
        if (cacheData.vehicles) {
          for (const [key, value] of Object.entries(cacheData.vehicles)) {
            if (now - value.timestamp < this.config.CONVOY_DETAILS_TTL) {
              this.vehicleCache.set(key, value);
            }
          }
        }
        
        // Load driver cache
        if (cacheData.drivers) {
          for (const [key, value] of Object.entries(cacheData.drivers)) {
            if (now - value.timestamp < this.config.CONVOY_DETAILS_TTL) {
              this.driverCache.set(key, value);
            }
          }
        }
        
        // Load site cache
        if (cacheData.sites) {
          for (const [key, value] of Object.entries(cacheData.sites)) {
            if (now - value.timestamp < this.config.CONVOY_DETAILS_TTL) {
              this.siteCache.set(key, value);
            }
          }
        }
        
        // Load all drivers cache
        if (cacheData.allDrivers && cacheData.allDriversTimestamp) {
          if (now - cacheData.allDriversTimestamp < this.config.CONVOY_DETAILS_TTL) {
            this.allDriversCache = cacheData.allDrivers;
            this.allDriversCacheTimestamp = cacheData.allDriversTimestamp;
          }
        }
        
        console.log(`📦 Loaded convoy details cache: ${this.vehicleCache.size} vehicles, ${this.driverCache.size} drivers, ${this.siteCache.size} sites`);
      }
    } catch (error) {
      console.warn('⚠️ Failed to load convoy details cache from storage:', error);
    }
  }

  /**
   * Save cache to sessionStorage
   */
  saveToStorage() {
    try {
      const cacheData = {
        vehicles: {},
        drivers: {},
        sites: {},
        allDrivers: this.allDriversCache,
        allDriversTimestamp: this.allDriversCacheTimestamp,
      };
      
      this.vehicleCache.forEach((value, key) => {
        cacheData.vehicles[key] = value;
      });
      
      this.driverCache.forEach((value, key) => {
        cacheData.drivers[key] = value;
      });
      
      this.siteCache.forEach((value, key) => {
        cacheData.sites[key] = value;
      });
      
      sessionStorage.setItem(this.config.STORAGE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('⚠️ Failed to save convoy details cache to storage:', error);
    }
  }

  /**
   * Check if a cache entry is valid
   */
  isValid(timestamp) {
    if (!timestamp) return false;
    const now = Date.now();
    return now - timestamp < this.config.CONVOY_DETAILS_TTL;
  }

  /**
   * Get cached vehicle name
   * @param {string} vehicleId - Vehicle ID
   * @returns {string|null} - Cached vehicle name or null
   */
  getVehicle(vehicleId) {
    if (!vehicleId) return null;
    
    const key = this.getVehicleKey(vehicleId);
    const entry = this.vehicleCache.get(key);
    
    if (!entry || !this.isValid(entry.timestamp)) {
      if (entry) this.vehicleCache.delete(key);
      return null;
    }
    
    return entry.name;
  }

  /**
   * Set cached vehicle name
   * @param {string} vehicleId - Vehicle ID
   * @param {string} name - Vehicle name
   */
  setVehicle(vehicleId, name) {
    if (!vehicleId || !name) return;
    
    const key = this.getVehicleKey(vehicleId);
    this.vehicleCache.set(key, {
      name,
      timestamp: Date.now(),
    });
    this.saveToStorage();
  }

  /**
   * Get cached driver name
   * @param {string} driverId - Driver ID
   * @returns {string|null} - Cached driver name or null
   */
  getDriver(driverId) {
    if (!driverId) return null;
    
    const key = this.getDriverKey(driverId);
    const entry = this.driverCache.get(key);
    
    if (!entry || !this.isValid(entry.timestamp)) {
      if (entry) this.driverCache.delete(key);
      return null;
    }
    
    return entry.name;
  }

  /**
   * Set cached driver name
   * @param {string} driverId - Driver ID
   * @param {string} name - Driver name
   */
  setDriver(driverId, name) {
    if (!driverId || !name) return;
    
    const key = this.getDriverKey(driverId);
    this.driverCache.set(key, {
      name,
      timestamp: Date.now(),
    });
    this.saveToStorage();
  }

  /**
   * Get cached site name
   * @param {string} siteId - Site ID
   * @returns {string|null} - Cached site name or null
   */
  getSite(siteId) {
    if (!siteId) return null;
    
    const key = this.getSiteKey(siteId);
    const entry = this.siteCache.get(key);
    
    if (!entry || !this.isValid(entry.timestamp)) {
      if (entry) this.siteCache.delete(key);
      return null;
    }
    
    return entry.name;
  }

  /**
   * Set cached site name
   * @param {string} siteId - Site ID
   * @param {string} name - Site name
   */
  setSite(siteId, name) {
    if (!siteId || !name) return;
    
    const key = this.getSiteKey(siteId);
    this.siteCache.set(key, {
      name,
      timestamp: Date.now(),
    });
    this.saveToStorage();
  }

  /**
   * Get cached all drivers list
   * @returns {Array|null} - Cached drivers array or null
   */
  getAllDrivers() {
    if (!this.allDriversCache || !this.isValid(this.allDriversCacheTimestamp)) {
      this.allDriversCache = null;
      this.allDriversCacheTimestamp = null;
      return null;
    }
    
    console.log('✅ Cache hit for all drivers');
    return this.allDriversCache;
  }

  /**
   * Set cached all drivers list
   * @param {Array} drivers - Drivers array
   */
  setAllDrivers(drivers) {
    if (!drivers || !Array.isArray(drivers)) return;
    
    this.allDriversCache = drivers;
    this.allDriversCacheTimestamp = Date.now();
    
    // Also cache individual driver names
    drivers.forEach(driver => {
      const driverId = driver.user_id || driver.employee_id;
      const driverName = driver.user_name || driver.name;
      if (driverId && driverName) {
        this.setDriver(driverId, driverName);
      }
    });
    
    this.saveToStorage();
    console.log(`💾 Cached ${drivers.length} drivers`);
  }

  /**
   * Clear all cached data
   */
  clearAll() {
    this.vehicleCache.clear();
    this.driverCache.clear();
    this.siteCache.clear();
    this.allDriversCache = null;
    this.allDriversCacheTimestamp = null;
    
    try {
      sessionStorage.removeItem(this.config.STORAGE_KEY);
    } catch (error) {
      console.warn('⚠️ Failed to clear cache from storage:', error);
    }
    console.log('🗑️ Cleared all convoy details cache');
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      vehicles: this.vehicleCache.size,
      drivers: this.driverCache.size,
      sites: this.siteCache.size,
      hasAllDrivers: !!this.allDriversCache && this.isValid(this.allDriversCacheTimestamp),
    };
  }
}

// Export singleton instance
export default new ConvoyDetailsCache();

